<template>
  <ul class="cordel-accordion">
    <li
      v-for="(section) in visible"
      :id="(section.html && section.html.id) || undefined"
      :key="section._ID"
      :class="classes[section._ID]"
      tabindex="0"
      @keydown.self.enter.exact="toggle(section)"
      @keydown.self.space.prevent.exact="toggle(section)"
    >
      <typography-heading
        v-if="section.title"
        class="cordel-accordion-header"
        hierarchy="h2"
        @click.native="toggle(section)"
      >
        {{ section.title }}

        <font-awesome-icon
          icon="chevron-up"
          class="cordel-accordion-chevron"
        />

        <template v-slot:subtitle>
          {{ section.subtitle }}

          <slot :name="`${section._ID}-subtitle`" />
        </template>
      </typography-heading>

      <ul class="cordel-accordion-slot">
        <slot :name="`${section._ID}-subheader`" />

        <information-line
          v-for="(field, index) in lines(section)"
          :key="field._ID"

          :label="field.label"
          :value="field.value"

          :fields="lines(section)"
          :field="field"
          :index="index"

          :tag="field.tag"
        >
          <template
            v-if="$slots[`${section._ID}-${field._ID}-label`]"
            v-slot:label
          >
            <slot :name="`${section._ID}-${field._ID}-label`" />
          </template>

          <slot :name="`${section._ID}-${field._ID}-value`" />
        </information-line>

        <slot
          v-if="section._ID"
          :name="section._ID"
        />

        <cordel-accordion
          v-if="recursive(section)"
          :sections="section.recursive"
          :collapsed="collapsed"
        >
          <template
            v-for="(_, name) in $slots"
            v-slot:[name]
          >
            <slot :name="name" />
          </template>
        </cordel-accordion>
      </ul>
    </li>
  </ul>
</template>

<script>
  /* eslint no-underscore-dangle: ['error', { allow: ['_ID', '_COLLAPSED'] }] */

  import Vue from 'vue';

  import TypographyHeading from '@cordel/components/Typography/Heading.vue';
  import InformationLine from '@cordel/components/Information/Line.vue';

  export default {
    name: 'CordelAccordion',
    components: {
      InformationLine,
      TypographyHeading,
    },
    props: {
      sections: {
        type: [Array, Object],
        default() {
          return [];
        },
      },
      collapsed: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        toggled: {},
      };
    },
    computed: {
      hash() {
        return this.$route.hash.replace('#', '');
      },
      visible() {
        return Object.values(this.sections)
          .filter(i => this.recursive(i) || this.lines(i).length || this.$slots[i._ID])
          .map(section => (section._ID ? section : {
            ...section,
            id: `${section.title}-${section.subtitle}|FALLBACK`,
          }));
      },
      classes() {
        return this.visible
          .reduce((reduced, section) => {
            let collapsed = false;

            if (typeof this.toggled[section._ID] === 'boolean') {
              collapsed = this.toggled[section._ID];
            } else if (typeof section._COLLAPSED === 'boolean') {
              collapsed = section._COLLAPSED;
            } else if (this.hash && section.html && section.html.id === this.hash) {
              collapsed = false;
            } else if (this.collapsed) {
              collapsed = true;
            }

            reduced[section._ID] = ['cordel-accordion-section'];

            if (collapsed) {
              reduced[section._ID].push('state--collapsed');
            }

            return reduced;
          }, {});
      },
    },
    methods: {
      recursive(section) {
        return section.recursive && section.recursive.length;
      },
      lines(section) {
        return Object
          .values(section.fields || {})
          .filter(field => field.type !== 'hidden' && (typeof field.visible === 'boolean' ? field.visible : true));
      },
      toggle(section) {
        if (typeof this.toggled[section._ID] !== 'boolean') {
          Vue.set(this.toggled, section._ID, !this.classes[section._ID].includes('state--collapsed'));
        } else {
          this.toggled[section._ID] = !this.toggled[section._ID];
        }
      },
    },
  };
</script>

<style lang="scss">
  .cordel-accordion-header {
    .cordel-typography-title {
      align-items: center;
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      .cordel-accordion .cordel-accordion & {
        font-size: var(--cordel-font-size-l);
      }
    }

    .cordel-alert {
      margin-top: var(--cordel-spacing-m);
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-accordion {
    list-style: none;
  }

  .cordel-accordion-section {
    background-color: var(--cordel-color-neutral-lighter);
    outline: none;

    &:focus {
      box-shadow: 0 0 0 var(--cordel-border-width-thin) var(--cordel-color-highlight-primary);
      position: relative;
    }

    .cordel-accordion .cordel-accordion & {
      background-color: var(--cordel-color-constant-light);
    }

    &:last-child {
      border-bottom: var(--cordel-border-width-thin) solid var(--cordel-color-constant-dark);

      &:focus {
        border-bottom-color: transparent;
      }
    }
  }

  .cordel-accordion-header {
    cursor: pointer;
    border-top: var(--cordel-border-width-thin) solid var(--cordel-color-constant-dark);
    padding: var(--cordel-spacing-xl) var(--cordel-spacing-xs);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xl) var(--cordel-spacing-xxxs);
    }

    &:hover {
      background-color: var(--cordel-color-neutral-light);
    }

    .cordel-accordion-section:last-child:focus > & {
      border-bottom-color: transparent;
    }

    .cordel-accordion-section:focus > &,
    .cordel-accordion-section:focus + .cordel-accordion-section > & {
      border-top-color: transparent;
    }
  }

  .cordel-accordion-slot {
    list-style: none;
    padding: var(--cordel-spacing-xs) var(--cordel-spacing-xs) var(--cordel-spacing-blaster);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xxxs) var(--cordel-spacing-xxxs) var(--cordel-spacing-blaster);
    }

    .state--collapsed > & {
      display: none;
    }
  }

  .cordel-accordion-chevron {
    transition: transform .1s;

    .state--collapsed & {
      transform: rotate(180deg);
    }
  }
</style>
